// GtAddToBenchModal — put any number of people on a bench, into the bucket you
// choose. A new bench or a new bucket can be made right here. Used from the
// Candidates table; built to be reused wherever people are ticked.
// A bench never contacts anyone, so this sends nothing to the candidates.
import { useState, useEffect } from 'react';
import { GtSelect } from './GtControls';

const NEW = '__new';
const BUCKET_TONES = ['teal', 'green', 'blue', 'slate', 'purple'];

export default function GtAddToBenchModal({ candidateIds, authFetch, flash, onClose, onDone, source = 'candidates_table', sourceRoleId = null, navigate }) {
  const [benches, setBenches] = useState(null);
  const [benchId, setBenchId] = useState('');
  const [newBench, setNewBench] = useState('');
  const [buckets, setBuckets] = useState([]);
  const [bucketId, setBucketId] = useState('');
  const [newBucket, setNewBucket] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const json = { 'Content-Type': 'application/json' };

  useEffect(() => {
    authFetch('/api/benches?status=active')
      .then(r => { const list = r.benches || []; setBenches(list); setBenchId(list.length ? list[0].id : NEW); })
      .catch(() => { setBenches([]); setBenchId(NEW); });
  }, [authFetch]);

  useEffect(() => {
    if (!benchId || benchId === NEW) { setBuckets([]); setBucketId(NEW); return; }
    authFetch('/api/benches/' + benchId)
      .then(r => { const b = r.buckets || []; setBuckets(b); setBucketId(b.length ? b[0].id : NEW); })
      .catch(() => { setBuckets([]); setBucketId(NEW); });
  }, [authFetch, benchId]);

  const needBenchName = benchId === NEW && !newBench.trim();
  const needBucketName = bucketId === NEW && !newBucket.trim();

  async function go() {
    setBusy(true);
    try {
      let bench = null;
      if (benchId === NEW) {
        const r = await authFetch('/api/benches', { method: 'POST', headers: json, body: JSON.stringify({ name: newBench.trim() }) });
        bench = r.bench;
      } else {
        bench = (benches || []).find(b => b.id === benchId) || { id: benchId };
      }
      let stageId = bucketId;
      if (bucketId === NEW) {
        const current = benchId === NEW ? [] : buckets;
        const label = newBucket.trim();
        const r = await authFetch('/api/benches/' + bench.id + '/buckets', {
          method: 'PUT', headers: json,
          body: JSON.stringify({ buckets: current.map(b => ({ id: b.id, label: b.label, tone: b.tone }))
            .concat([{ label, tone: BUCKET_TONES[current.length % BUCKET_TONES.length] }]) }),
        });
        const made = (r.buckets || []).find(b => b.label === label);
        stageId = made ? made.id : null;
      }
      const r = await authFetch('/api/benches/' + bench.id + '/members', {
        method: 'POST', headers: json,
        body: JSON.stringify({ candidate_ids: candidateIds, stage_id: stageId == null ? null : Number(stageId), source, source_role_id: sourceRoleId }),
      });
      setResult({ ...r, bench });
      if (onDone) onDone();
    } catch (e) { flash((e.body && e.body.message) || e.message); }
    setBusy(false);
  }

  const benchOpts = (benches || []).map(b => ({ value: b.id, label: b.name + (b.client_name ? ' · ' + b.client_name : '') }))
    .concat([{ value: NEW, label: '+ A new bench' }]);
  const bucketOpts = buckets.map(b => ({ value: b.id, label: b.label })).concat([{ value: NEW, label: '+ A new bucket' }]);
  const n = candidateIds.length;

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Add {n === 1 ? '1 person' : n + ' people'} to a bench</div>
        <div className="gtc-modal-body">
          {result ? (
            <div className="gtnt-banner gtnt-banner--green">
              <b>{result.added} added to {result.bench.name || 'the bench'}.</b>
              {result.already_there > 0 && ' ' + result.already_there + ' were already on it and were left as they were.'}
              {result.not_found > 0 && ' ' + result.not_found + ' could not be found.'}
              <p className="gtbn-help">Nobody has been contacted.</p>
            </div>
          ) : benches === null ? <div className="gtr-empty-sm">Loading benches…</div> : (
            <>
              <p className="gtbn-help">They go into the bucket you pick. A bench never contacts anyone.</p>
              <div className="gtc-field"><span>Bench</span>
                <GtSelect value={benchId} onChange={setBenchId} options={benchOpts} />
              </div>
              {benchId === NEW && (
                <label className="gtc-field"><span>New bench name</span>
                  <input autoFocus value={newBench} maxLength={120} placeholder="e.g. Eval engineers for Q1" onChange={e => setNewBench(e.target.value)} />
                </label>
              )}
              <div className="gtc-field"><span>Bucket</span>
                <GtSelect value={bucketId} onChange={setBucketId} options={bucketOpts} />
              </div>
              {bucketId === NEW && (
                <label className="gtc-field"><span>New bucket name</span>
                  <input value={newBucket} maxLength={60} placeholder="e.g. Strong fit" onChange={e => setNewBucket(e.target.value)} />
                </label>
              )}
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          {result ? (
            <>
              {navigate && result.bench.ref && <button className="gtc-btn-ghost" onClick={() => navigate('/benches/' + result.bench.ref)}>Open the bench</button>}
              <button className="gtc-primary" onClick={onClose}>Done</button>
            </>
          ) : (
            <>
              <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
              <button className="gtc-primary" disabled={busy || benches === null || needBenchName || needBucketName} onClick={go}>
                {busy ? 'Adding…' : 'Add ' + n}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
